import { Box } from "@mui/material";
import { memo, useEffect, useState, useMemo } from "react";

import { useAppContext } from "../../../2_ksbHub/context/appContext";
import { useSondertagContext } from "../context/sondertagContext";

import JahrgangHeader from "./JahrgangHeader";
import JahrgangActions from "./JahrgangActions";
import JahrgangGrid from "./JahrgangGrid";

import AngebotDialog from "./AngebotDialog";
import useSondertagActions from "../hooks/useSondertagActions";

function JahrgangCard({
  jahrgang,
  sondertagId,
  viewMode,
  onEdit,
  onDelete,
  allAngebote,
}) {
  const { currentUser, isTeacherView, isMobile } = useAppContext();
  const { isASondertageEventAdmin } = useSondertagContext();

  const {
    handleSaveAngebot,
    handleLockAngebot,
    handleUnlockAngebot,
    handleDeleteAngebot,
  } = useSondertagActions();

  const [expanded, setExpanded] = useState(false);

  const angebote = useMemo(() => jahrgang.angebote || [], [jahrgang.angebote]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  /**
   * =========================================================
   * ✅ DERIVED DATA
   * =========================================================
   */

  // angebote.forEach((a) => {
  //   console.log(
  //     a.title,
  //     a.owners?.map((o) => o.abbreviation),
  //   );
  // });

  const hasMyReservation = useMemo(
    () => angebote.some((a) => a.owners?.some((o) => o.id === currentUser?.id)),
    [angebote, currentUser?.id],
  );

  const hasLeerReservations = useMemo(
    () =>
      angebote.some(
        (a) =>
          a.owners?.length === 1 &&
          a.owners[0]?.abbreviation?.toLowerCase() === "leer",
      ),
    [angebote],
  );

  const hasFree = useMemo(
    () => angebote.some((a) => !a.owners || a.owners.length === 0),
    [angebote],
  );

  /**
   * =========================================================
   * ✅ TILE CLICK
   * =========================================================
   */
  const handleTileClick = (angebot, klasseId, halbtag) => {
    // ✅ prevent interaction if locked
    if (angebot?.lockedBy && angebot.lockedBy !== currentUser?.id) return;

    // ✅ lock BEFORE opening dialog
    if (angebot?.id) {
      handleLockAngebot(angebot.id);
    }

    setSelected({ angebot, klasseId, halbtag });
    setDialogOpen(true);
  };

  /**
   * =========================================================
   * ✅ CLOSE DIALOG
   * =========================================================
   */
  const handleClose = () => {
    if (selected?.angebot?.id) {
      handleUnlockAngebot(selected.angebot.id);
    }

    setDialogOpen(false);
    setSelected(null);
  };

  /**
   * =========================================================
   * ✅ SAVE (RESERVATION)
   * =========================================================
   */

  const handleSave = async (data) => {
    try {
      const result = await handleSaveAngebot({
        data: {
          ...data,
          sondertagId,
        },
        sondertagId,
        existingAngebot: selected.angebot,
        jahrgangId: jahrgang.id,
        angebote: allAngebote, // ✅ use GLOBAL
      });

      if (result?.skipped?.length) {
        console.warn("Skipped slots:", result.skipped);
        // 👉 later: show snackbar
      }
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      handleClose();
    }
  };

  /**
   * =========================================================
   * ✅ EXPANSION LOGIC
   * =========================================================
   */

  useEffect(() => {
    if (viewMode === "mine") {
      setExpanded(hasMyReservation);
    } else if (viewMode === "leitung") {
      // setExpanded(hasFree);
      setExpanded(false);
    } else if (viewMode === "leer") {
      // setExpanded(hasLeerReservations);
      setExpanded(false);
    } else if (viewMode === "all") {
      setExpanded(false);
    }
  }, [viewMode, hasMyReservation, hasLeerReservations, hasFree]);

  /**
   * =========================================================
   * ✅ RENDER
   * =========================================================
   */

  const shouldRender =
    viewMode === "all" ||
    (viewMode === "mine" && hasMyReservation) ||
    (viewMode === "leitung" && hasFree) ||
    (viewMode === "leer" && hasLeerReservations);

  if (!shouldRender) return null;

  return (
    <Box sx={{ mt: 2, p: 1.5 }}>
      {/* ✅ HEADER */}
      <JahrgangHeader
        jahrgang={jahrgang}
        onToggle={() => setExpanded((p) => !p)}
      />

      {/* ✅ CONTENT */}
      {expanded && (
        <>
          <JahrgangGrid
            jahrgang={jahrgang}
            angebote={angebote}
            onTileClick={handleTileClick}
            viewMode={viewMode}
          />

          <JahrgangActions
            jahrgang={jahrgang}
            isMobile={isMobile}
            isAdmin={isASondertageEventAdmin && !isTeacherView}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </>
      )}

      {/* ✅ DIALOG */}
      <AngebotDialog
        open={dialogOpen}
        onClose={handleClose}
        onSave={handleSave}
        onUnreserve={async (angebot) => {
          await handleSave({
            id: angebot.id,
            title: "",
            owners: [], // ✅ THIS is the key
            klasseId: selected?.klasseId,
            halbtag: selected?.halbtag,
          });

          handleClose();
        }}
        onDelete={handleDeleteAngebot}
        angebot={selected?.angebot}
        klasseId={selected?.klasseId}
        halbtag={selected?.halbtag}
      />
    </Box>
  );
}

export default memo(JahrgangCard);
